import { useCallback, useEffect, useState } from "react";
import { RefreshCwIcon } from "lucide-react";
import { MatchStatsView, type MatchStatsData, type MatchStatsPeriod } from "@dolfin/ui";
import { api } from "@/api/client";

// No /admin/* read route needed: OrganisationIdMiddleware lets staff pin any org or client by header.
// The cache refresh is an operational lever, so it uses an /admin/* route, off the public v1 doc.
export function MatchStatsPanel({
  clientId,
  organisationId,
}: {
  clientId?: string;
  /** When present the read is organisation-scoped; `clientId` still addresses the cache refresh (the entry lives under the client). */
  organisationId?: string;
}) {
  const [data, setData] = useState<MatchStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<MatchStatsPeriod>("Last30Days");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const request = organisationId
      ? api.GET("/v1/match-stats", {
          params: { query: { period } },
          headers: { "x-dolfin-organisation-id": organisationId },
        })
      : api.GET("/v1/client/match-stats", {
          params: { query: { period } },
          ...(clientId ? { headers: { "x-dolfin-client-id": clientId } } : {}),
        });

    request
      .then(({ data: body, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !body) {
          setError("The match statistics could not be loaded.");
          return;
        }
        setData(body as MatchStatsData);
      })
      .catch(() => {
        if (!cancelled) setError("The match statistics could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, organisationId, period]);

  useEffect(() => load(), [load]);

  // Drop the cache, then re-read: the endpoint invalidates without recomputing, so the reverse order needs two clicks.
  const handleRefresh = useCallback(async () => {
    if (!clientId) return;
    setRefreshing(true);
    setRefreshError(null);

    const { error: apiError } = await api.POST("/admin/clients/{clientId}/match-stats/refresh", {
      params: { path: { clientId } },
    });

    setRefreshing(false);

    if (apiError) {
      // The cache was not dropped; re-reading would look like a silent success.
      setRefreshError("The cached statistics could not be cleared. The figures below may still be stale.");
      return;
    }

    load();
  }, [clientId, load]);

  return (
    <div className="flex flex-col gap-2">
      <MatchStatsView
        data={data}
        loading={loading}
        error={error}
        period={period}
        onPeriodChange={setPeriod}
        onRetry={load}
        actions={
          clientId ? (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              title="Discard the cached figures and recompute from the database. Match stats are cached for an hour, so a rematch or a settings change will not show up here until this is pressed."
              className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-1 text-xs font-medium transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCwIcon className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Recalculating..." : "Recalculate"}
            </button>
          ) : undefined
        }
        dense
        showCounts
      />
      {refreshError && <p className="text-xs text-destructive">{refreshError}</p>}
    </div>
  );
}
